import React, { MouseEvent } from 'react';
import moment from 'moment';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSnackbar } from 'notistack';
import { Box } from '@material-ui/core';

import { pxToRem } from 'src/helpers/pxToRem';
import { useIsNewInvoicingFlow } from 'src/components/NewInvoicing/hooks/useIsNewInvoicingFlow';

import Score from '../Score';
import { WarningIcon } from '../../../assets/svgs/warning';
import { ReactComponent as CheckMark } from '../../../assets/svgs/checkmark.svg';
import { ClientSummaryNode } from '../../../generated/webapp_gql';
import { getHpiCoverageDisplayMsg, getMedicationManagementDisplayMsg } from './helpers';

interface Props {
  client: ClientSummaryNode;
  isCurrentlySelected: boolean;
  isNewMatch: boolean;
  isSelectedForBulkMessaging: boolean;
  matched: boolean;
  onReportVisit: (e: MouseEvent) => void;
  onSelectChat: (e: MouseEvent) => void;
  onSelectMember: () => void;
  onSelectRow: () => void;
  providerIsTherapist: boolean;
  shouldAddColumnForMemberDrawer: boolean;
  showSelectBox: boolean;
  providerIsPsychiatrist: boolean;
}

export enum ClientRowTestID {
  SelectMemberCheckBox = 'SelectMemberCheckBox',
  OpenMemberProfileDrawer = 'OpenMemberProfileDrawer',
  ChatNowButton = 'ChatNowButton',
  ReportVisitButton = 'ReportVisitButton',
  MemberName = 'ClientRowTestID.MemberName',
  MemberEmail = 'ClientRowTestID.MemberEmail',
  Company = 'ClientRowTestID.Company',
  MatchStatus = 'ClientRowTestID.MatchStatus',
  BookedSessions = 'ClientRowTestID.BookedSessions',
  MostRecentWho5 = 'ClientRowTestID.MostRecentWho5',
  MemberInsuranceStatus = 'ClientRowTestID.MemberInsuranceStatus',
  MemberMedicationManagementAvailable = 'ClientRowTestID.MemberMedicationManagementAvailable',
  RemainingSessionsColumn = 'ClientRowRemainingSessionsColumn',
}

export const ClientRow: React.FC<Props> = ({
  client,
  isCurrentlySelected,
  isNewMatch,
  isSelectedForBulkMessaging,
  matched,
  onReportVisit,
  onSelectChat,
  onSelectMember,
  onSelectRow,
  providerIsPsychiatrist,
  shouldAddColumnForMemberDrawer,
  showSelectBox,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { showHpiClientStatusInfo, showMemberPhq9SelfharmScore } = useFlags();
  const remainingVisits = Math.max(client.visitsRemaining, 0);
  const isNewInvoicingFlow = useIsNewInvoicingFlow();

  const handleRowClick = () => {
    if (!matched) return;

    onSelectRow();
  };

  function HandleOnReportVisit(e) {
    // Prevents the SelectRow event.
    e.stopPropagation();

    if (isNewInvoicingFlow && client.coachingVisitsToReportCount === 0) {
      enqueueSnackbar(
        `There are currently no valid sessions to report for ${client.memberFirstName} ${client.memberLastName}`,
        {
          variant: 'error',
        },
      );
    } else {
      onReportVisit(e);
    }
  }

  return (
    <tr
      className={`tr-clients ${
        matched ? 'cursor-pointer hover:bg-gray-200' : 'text-gray-600 bg-gray-100'
      } ${isCurrentlySelected && 'bg-gray-200'}`}
      data-testid={ClientRowTestID.OpenMemberProfileDrawer}
      onClick={handleRowClick}
    >
      {showSelectBox && (
        <td className="td-clients">
          <div
            data-testid={ClientRowTestID.SelectMemberCheckBox}
            onClick={(e) => {
              e.stopPropagation();
              onSelectMember();
            }}
            className={`h-4 w-4 ${
              isSelectedForBulkMessaging ? 'bg-blue-200' : 'border border-gray-400 bg-white'
            } rounded flex items-center justify-center`}
          >
            {isSelectedForBulkMessaging && <CheckMark className="h-3 w-3" />}
          </div>
        </td>
      )}

      <td className={`td-clients ${isNewMatch && 'min-w-1/6'}`}>
        <div className="mb-1 flex flex-row justify-between">
          <Box
            className="font-bold text-sm"
            data-testid={ClientRowTestID.MemberName}
            display="flex"
          >
            {showMemberPhq9SelfharmScore && !!client.phq9SelfharmScore && (
              <Box display="inline-block" marginRight={pxToRem(5)}>
                <WarningIcon />
              </Box>
            )}
            <span>
              {client.memberFirstName} {client.memberLastName}
            </span>
          </Box>
        </div>

        <p className="text-gray-500" data-testid={ClientRowTestID.MemberEmail}>
          {client.memberEmail}
        </p>
      </td>

      <td className="td-clients pl-0" data-testid={ClientRowTestID.Company}>
        {client.companyName}
      </td>

      <td className="td-clients" data-testid={ClientRowTestID.MatchStatus}>
        {client.matchStatus}
      </td>

      <td className="td-clients" data-testid={ClientRowTestID.BookedSessions}>
        <div>
          <p>Last: {client.lastVisit ? moment(client.lastVisit).format('ll') : 'None'}</p>

          <p className="whitespace-no-wrap">
            Next: {client.nextVisit ? moment(client.nextVisit).format('ll') : 'Not Scheduled'}
          </p>
        </div>
      </td>

      {!providerIsPsychiatrist && (
        <td className="td-clients" data-testid={ClientRowTestID.RemainingSessionsColumn}>
          {remainingVisits === undefined ? '' : `${remainingVisits} of `}

          {client.visitsCovered}
        </td>
      )}

      {showHpiClientStatusInfo && (
        <td className="td-clients" data-testid={ClientRowTestID.MemberInsuranceStatus}>
          {getHpiCoverageDisplayMsg(client)}
        </td>
      )}

      <td className="td-clients" data-testid={ClientRowTestID.MemberMedicationManagementAvailable}>
        {getMedicationManagementDisplayMsg(client)}
      </td>

      <td className="td-clients" data-testid={ClientRowTestID.MostRecentWho5}>
        <Score score={client.lastWho5Score ?? undefined} disabled={!matched} />
      </td>

      <td className="td-clients pl-0">
        {matched && (
          <button
            className="flex w-full font-extrabold justify-center text-white p-2 rounded hover:shadow-md bg-blue-200 whitespace-no-wrap"
            data-testid={ClientRowTestID.ChatNowButton}
            onClick={(e) => {
              e.stopPropagation();
              onSelectChat(e);
            }}
            disabled={!client.appInstalled}
          >
            Chat
          </button>
        )}
      </td>

      <td className="td-clients pl-0">
        {matched && (
          <button
            className="flex w-full font-extrabold justify-center text-blue-200 p-2 rounded border border-blue-200 whitespace-no-wrap"
            data-testid={ClientRowTestID.ReportVisitButton}
            onClick={HandleOnReportVisit}
          >
            Report Visit
          </button>
        )}
      </td>

      {shouldAddColumnForMemberDrawer && <td className="td-clients w-100" />}
    </tr>
  );
};
